import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CalendarCheck, Download } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { assessmentService, schedulerService, userService, divisionService } from '@services';
import { useSchedulePageState } from '@hooks/useSchedulePageState';
import { useSchedulePageModals } from '@hooks/useSchedulePageModals';
import { useSchedulePageData } from '@hooks/useSchedulePageData';
import { useSchedulePageHandlers } from '@hooks/useSchedulePageHandlers';
import { useScheduleConflictFilter } from '@hooks/useScheduleConflictFilter';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import Tabs, { TabItem } from '@components/common/Tabs';
import AssessorMeetingsTable from '@components/features/assessments/AssessorMeetingsTable';
import { FilterButtons, FilterOption } from '@components/common/FilterButtons';
import { MeetingsCalendar, CalendarFilters } from '@components/features/calendar';
import { useCalendarEvents } from '@hooks/useCalendarEvents';
import ScheduledDateModal from '@components/common/ScheduledDateModal';
import AssessorAssignmentModal from '@components/common/AssessorAssignmentModal';
import MultiRoleAssignmentModal from '@components/common/MultiRoleAssignmentModal';
import AddToGroupModal from '@components/common/AddToGroupModal';
import ConfirmDialog from '@components/common/ConfirmDialog';
import { Pagination, ColumnSelector } from '@components/common/table';
import Button from '@components/common/Button';
import { ScheduleItem, TableColumn } from '@app-types';
import { STATUS_LABELS } from '@utils/statusUtils';
import { exportScheduleItems } from '@utils/exportUtils';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import { LoadingState, LoadingOverlay } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';

// Base meeting status filter options (will be made dynamic in component)
const baseStatusFilters: FilterOption[] = [
  { key: 'scheduled', label: STATUS_LABELS['scheduled'] },
  { key: 'rescheduled', label: STATUS_LABELS['rescheduled'] },
  { key: 'completed', label: STATUS_LABELS['completed'] }
];

// Column configuration for meeting list table
const columns: TableColumn[] = [
  {
    key: 'contract_number',
    label: 'Contract Number',
    sortable: true,
    width: '130px',
    defaultVisible: true,
    alwaysVisible: true,
    filterKey: 'contract_number'
  },
  { key: 'contract_name', label: 'Contract Name', sortable: false, defaultVisible: false, filterable: false },
  {
    key: 'contract_start_date',
    label: 'Contract Start Date',
    sortable: false,
    defaultVisible: false,
    filterable: false
  },
  { key: 'contract_end_date', label: 'Contract End Date', sortable: false, defaultVisible: false, filterable: false },
  { key: 'contract_amount', label: 'Contract Amount', sortable: false, defaultVisible: false, filterable: false },
  { key: 'contract_type', label: 'Contract Type', sortable: false, defaultVisible: false, filterable: false },
  {
    key: 'meeting_date',
    label: 'Meeting Date',
    sortable: true,
    width: '130px',
    defaultVisible: true,
    filterable: false
  },
  {
    key: 'meeting_status',
    label: 'Meeting Status',
    sortable: true,
    width: '120px',
    defaultVisible: true,
    filterKey: 'meeting_status'
  },
  {
    key: 'assessment_id',
    label: 'Assessment #',
    sortable: true,
    width: '120px',
    defaultVisible: true,
    filterKey: 'assessment_id'
  },
  {
    key: 'assessment_name',
    label: 'Assessment Name',
    sortable: true,
    width: '200px',
    defaultVisible: true,
    filterKey: 'assessment_name'
  },
  {
    key: 'assessment_status',
    label: 'Assessment Status',
    sortable: true,
    width: '140px',
    defaultVisible: true,
    filterKey: 'assessment_status'
  },
  {
    key: 'supplier_name',
    label: 'Supplier',
    sortable: true,
    width: '180px',
    defaultVisible: true,
    filterKey: 'supplier'
  },
  { key: 'tier', label: 'Tier', sortable: true, width: '80px', defaultVisible: true, filterKey: 'tier' },
  { key: 'division', label: 'Division', sortable: true, width: '150px', defaultVisible: true, filterKey: 'division' },
  { key: 'cost_center', label: 'Cost Center', sortable: false, defaultVisible: false, filterable: false },
  {
    key: 'evidence_status',
    label: 'Evidence Status',
    sortable: true,
    width: '140px',
    defaultVisible: true,
    filterKey: 'evidence_status'
  },
  {
    key: 'evidence_due_date',
    label: 'Evidence Due',
    sortable: true,
    width: '130px',
    defaultVisible: true,
    filterable: false
  },
  {
    key: 'assessor',
    label: 'Assessor Name',
    sortable: true,
    width: '180px',
    defaultVisible: true,
    filterKey: 'assessor_name'
  },
  {
    key: 'contract_managers',
    label: 'Contract Manager(s)',
    sortable: true,
    width: '180px',
    defaultVisible: true,
    filterKey: 'contract_manager'
  },
  {
    key: 'contract_owners',
    label: 'Contract Owner(s)',
    sortable: true,
    width: '180px',
    defaultVisible: true,
    filterKey: 'contract_owner'
  },
  {
    key: 'in_progress',
    label: 'Scheduled By',
    sortable: true,
    width: '180px',
    defaultVisible: true,
    filterKey: 'processed_by'
  },
  {
    key: 'scheduling_notes',
    label: 'Scheduling Notes',
    sortable: false,
    defaultVisible: false,
    filterable: false
  },
  {
    key: 'rescheduled_date',
    label: 'Rescheduled Meeting Date',
    sortable: false,
    defaultVisible: false,
    filterable: false
  },
  {
    key: 'rescheduled_reason',
    label: 'Rescheduled Reason',
    sortable: false,
    defaultVisible: false,
    filterable: false
  },
  {
    key: 'view',
    label: '',
    sortable: false,
    width: '40px',
    defaultVisible: true,
    hiddenFromSelector: true,
    filterable: false
  }
];

// Column to field mapping for sorting (matches backend fields)
const columnSortMapping: { [key: string]: string } = {
  contract_number: 'contract_number',
  meeting_date: 'meeting_date',
  meeting_status: 'meeting_status',
  assessment_id: 'assessment_id',
  assessment_name: 'assessment_name',
  assessment_status: 'assessment_status',
  supplier_name: 'supplier_name',
  tier: 'tier',
  division: 'division_name',
  assessor: 'assessor_name',
  contract_managers: 'contract_manager',
  contract_owners: 'contract_owner',
  evidence_status: 'evidence_status',
  evidence_due_date: 'evidence_due_date',
  in_progress: 'processed_by'
};

const pageTabs: TabItem[] = [
  { key: 'list', label: 'List' },
  { key: 'calendar', label: 'Calendar' },
];

const AssessorMeetingsPage: React.FC = () => {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state synced with URL
  const requestedTab = searchParams.get('tab');
  const activeTab = pageTabs.some((t) => t.key === requestedTab) ? (requestedTab as string) : 'list';

  const setActiveTab = (tabKey: string) => {
    const next = new URLSearchParams(searchParams);
    if (tabKey === 'list') {
      next.delete('tab');
    } else {
      next.set('tab', tabKey);
    }
    setSearchParams(next, { replace: true });
  };

  // Calendar tab state
  const [calendarDateRange, setCalendarDateRange] = useState<{ start: string; end: string }>({
    start: new Date().toISOString().split('T')[0],
    end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  const [calendarFilters, setCalendarFilters] = useState({
    includeCompleted: false,
    division: '',
    tier: '',
    assessorId: '',
    status: '',
  });

  // Fetch assessors and divisions for calendar filter dropdowns
  const { data: assessorUsers } = useQuery({
    queryKey: ['users', 'Assessor'],
    queryFn: () => userService.getUsers('Assessor'),
    staleTime: 10 * 60 * 1000,
    enabled: activeTab === 'calendar',
  });

  const { data: activeDivisions } = useQuery({
    queryKey: ['divisions', 'active'],
    queryFn: () => divisionService.getActive(),
    staleTime: 10 * 60 * 1000,
    enabled: activeTab === 'calendar',
  });

  const assessorOptions = useMemo(() => [
    { value: '', label: 'All assessors' },
    ...(Array.isArray(assessorUsers) ? assessorUsers : []).map((u) => ({
      value: String(u.id),
      label: `${u.first_name} ${u.last_name}`,
    })),
  ], [assessorUsers]);

  const divisionOptions = useMemo(() => [
    { value: '', label: 'All divisions' },
    ...(Array.isArray(activeDivisions) ? activeDivisions : []).map((d) => ({
      value: d.name,
      label: d.name,
    })),
  ], [activeDivisions]);

  const { data: calendarData, isLoading: calendarIsLoading, isFetching: calendarIsFetching } = useCalendarEvents({
    filters: {
      startDate: calendarDateRange.start,
      endDate: calendarDateRange.end,
      includeCompleted: calendarFilters.includeCompleted || undefined,
      division: calendarFilters.division || undefined,
      tier: calendarFilters.tier || undefined,
      assessorId: calendarFilters.assessorId ? Number(calendarFilters.assessorId) : undefined,
      status: calendarFilters.status || undefined,
    },
    enabled: activeTab === 'calendar',
  });

  const handleCalendarDatesChange = useCallback((start: string, end: string) => {
    setCalendarDateRange({ start, end });
  }, []);

  const handleCalendarFilterChange = useCallback((newFilters: typeof calendarFilters) => {
    setCalendarFilters(newFilters);
  }, []);

  // Use generic hooks for page management
  const {
    searchQuery,
    debouncedSearchQuery,
    handleSearch,
    filter: statusFilter,
    handleFilterChange,
    checkboxFilter: myMeetingsOnly,
    handleCheckboxChange: handleMyMeetingsChange,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortBy,
    sortOrder,
    handleColumnSort,
    getSortIndicator,
    isColumnSorted,
    columns: allColumns,
    visibleColumns,
    renderedColumns,
    toggleColumn,
    // Column filters
    columnFilterValues,
    debouncedColumnFilters,
    setColumnFilter,
    clearColumnFilters,
    hasActiveColumnFilters,
    getColumnFiltersParam
  } = useSchedulePageState({
    defaultFilter: 'scheduled',
    defaultSort: 'scheduled_datetime',
    defaultSortOrder: 'ASC',
    // Admin sees ALL meetings by default (unchecked), Assessor sees only their meetings (checked)
    defaultCheckboxFilter: !permissions.isAdmin,
    columnSortMapping,
    columns,
    columnStorageKey: 'assessor-meetings-visible-columns'
  });

  // Use generic hook for modals
  const {
    showScheduledDateModal,
    showAssessorModal,
    showManagerModal,
    showOwnerModal,
    showAddToGroupModal,
    showRemoveConfirm,
    selectedItem,
    selectedContractId,
    selectedIsGroupOperation,
    groupToAddTo,
    removeGroupId,
    removeContractId,
    openScheduledDateModal,
    closeScheduledDateModal,
    openAssessorModal,
    closeAssessorModal,
    openManagerModal,
    closeManagerModal,
    openOwnerModal,
    closeOwnerModal,
    openAddToGroupModal,
    closeAddToGroupModal,
    openRemoveConfirm,
    closeRemoveConfirm
  } = useSchedulePageModals();

  // Map frontend column names to backend search column names
  // Backend response has: supplier_name, tier, contract_managers, contract_owners
  // But for SEARCH, backend expects: supplier, tier, contract_manager, contract_owner
  const mapColumnsForBackend = (frontendColumns: string[]): string[] => {
    const columnMapping: Record<string, string> = {
      supplier_name: 'supplier', // Backend search expects 'supplier'
      contract_managers: 'contract_manager', // Backend search expects singular
      contract_owners: 'contract_owner' // Backend search expects singular
      // Note: tier is already correct, no mapping needed
    };

    return frontendColumns.map((col) => columnMapping[col] || col);
  };

  // Use generic hook for data fetching
  const { scheduleItems, isLoading, error, isFetching, filterCounts, totalItems, totalPages, refreshData } =
    useSchedulePageData({
      queryKey: [
        'assessor-meetings',
        debouncedSearchQuery,
        statusFilter,
        myMeetingsOnly,
        sortBy,
        sortOrder,
        currentPage,
        pageSize,
        debouncedColumnFilters
      ],
      pageSize, // Pass pageSize for correct totalPages calculation
      queryFn: () => {
        const filters = {
          searchTerm: debouncedSearchQuery || undefined,
          searchColumns: debouncedSearchQuery ? mapColumnsForBackend(Array.from(visibleColumns)) : undefined,
          columnFilters: getColumnFiltersParam() || undefined,
          state: statusFilter,
          processedByMe: myMeetingsOnly || undefined,
          sortBy: sortBy,
          order: sortOrder as 'ASC' | 'DESC',
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
          viewMode: 'assessor' as const
        };
        return schedulerService.getScheduleItems(filters);
      }
    });

  // Use hook for conflict filter button management
  const { stateFilters: statusFilters } = useScheduleConflictFilter({
    filterCounts,
    isLoading,
    currentFilter: statusFilter,
    initialFilter: 'scheduled', // Default filter for Assessor page
    autoSwitchTargetFilter: 'scheduled', // Target when conflicts resolved
    handleFilterChange,
    debouncedSearchQuery,
    hasActiveColumnFilters,
    baseFilters: baseStatusFilters,
    hasOtherActiveFilters: myMeetingsOnly !== true // myMeetingsOnly=true is default for Assessor page
  });

  // Use generic hook for handlers
  const {
    handleAssignRole,
    handleScheduleDate,
    handleDateSelected,
    handleAssessorAssigned,
    handleManagerAssigned,
    handleOwnerAssigned,
    handleAddToGroup,
    handleAddContractsToGroup,
    handleRemoveFromGroup,
    confirmRemoveFromGroup
  } = useSchedulePageHandlers({
    selectedItem,
    selectedContractId,
    selectedIsGroupOperation,
    removeGroupId,
    removeContractId,
    openScheduledDateModal,
    closeScheduledDateModal,
    openAssessorModal,
    closeAssessorModal,
    openManagerModal,
    closeManagerModal,
    openOwnerModal,
    closeOwnerModal,
    openAddToGroupModal,
    closeAddToGroupModal,
    openRemoveConfirm,
    closeRemoveConfirm,
    refreshData,
    apiMethods: {
      updateOwners: schedulerService.updateContractOwners.bind(schedulerService),
      updateManagers: schedulerService.updateContractManagers.bind(schedulerService),
      updateAssessor: async (item: ScheduleItem, assessorId: number, reason?: string) => {
        if (!item.id) return;
        await schedulerService.updateScheduleItemAssessor(item.id, assessorId, undefined, reason);
      },
      scheduleOrReschedule: async (item: ScheduleItem, date: string, reason?: string, notes?: string) => {
        if (!item.id) return;
        if (date) {
          await schedulerService.scheduleScheduleItemMeeting(item.id, 0, date, reason, notes);
        } else {
          await schedulerService.deleteScheduleItemMeeting(item.id, 0, reason, notes);
        }
      },
      addToGroup: schedulerService.addContractsToGroup.bind(schedulerService),
      removeFromGroup: schedulerService.removeContractFromGroup.bind(schedulerService)
    }
  });

  // Handle create assessment
  const handleCreateAssessment = useCallback(
    async (item: ScheduleItem, createForGroup?: boolean, contractId?: number) => {
      try {
        let assessmentIdToNavigate: number;

        if (createForGroup && item.id) {
          // Create assessments for all contracts in group (clicked on parent row)
          const result = await assessmentService.createAssessmentsForGroupMeeting(item.id);
          console.log(`Created ${result.count} assessments for grouped meeting`);

          // Some contracts may have been skipped (e.g. assessor/CM conflict)
          // while the rest were created successfully - surface those reasons.
          result.errors?.forEach((err) => showToast.error(err.error));

          // Navigate to first assessment in the group
          assessmentIdToNavigate = result.assessment_ids[0];
        } else {
          // Create assessment for single contract (clicked on child row or single row)
          const meetingId = item.id || 0;
          // Use provided contractId, or fallback to first contract
          const targetContractId = contractId || item.contracts[0]?.id || 0;

          const result = await assessmentService.createAssessmentForMeeting(meetingId, targetContractId);
          console.log(`Created assessment ${result.assessment_id}: ${result.assessment_name}`);

          // Navigate to the created assessment
          assessmentIdToNavigate = result.assessment_id;
        }

        // Refresh data
        refreshData();

        // Navigate to assessment details page
        navigate(`/assessments/${assessmentIdToNavigate}`);
      } catch (error) {
        console.error('Failed to create assessment:', error);
        showToast.error(getApiErrorMessage(error, 'Failed to create assessment. Please try again.'));
      }
    },
    [refreshData, navigate]
  );

  // Export mutation - fetches the full filtered dataset (all pages), not just the current page
  const exportMutation = useMutation({
    mutationFn: () =>
      schedulerService.getScheduleItemsForExport({
        searchTerm: debouncedSearchQuery || undefined,
        searchColumns: debouncedSearchQuery ? mapColumnsForBackend(Array.from(visibleColumns)) : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        state: statusFilter,
        processedByMe: myMeetingsOnly || undefined,
        sortBy: sortBy,
        order: sortOrder as 'ASC' | 'DESC',
        viewMode: 'assessor'
      }),
    onSuccess: (response) => {
      exportScheduleItems(response.data, 'assessor-meetings', Array.from(visibleColumns), 'assessor-meetings');
    },
    onError: (err: unknown) => {
      console.error('Failed to export meetings:', err);
      showToast.error(getApiErrorMessage(err, 'Failed to export meetings. Please try again.'));
    }
  });

  if (error) {
    return (
      <PageLayout>
        <ErrorState message="Error loading meetings. Please try again." />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="contracts-page">
        <ListPageHeader
          icon={<CalendarCheck size={24} />}
          title="Assessment Meetings"
          count={filterCounts.all}
          searchValue={searchQuery}
          onSearchChange={handleSearch}
          searchPlaceholder="Search meetings, assessments, suppliers..."
          actions={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => exportMutation.mutate()}
                disabled={exportMutation.isPending || !scheduleItems || scheduleItems.length === 0}
              >
                <Download size={16} />
                {exportMutation.isPending ? 'Exporting...' : 'Export List'}
              </Button>
              <ColumnSelector
                columns={allColumns}
                visibleColumns={Array.from(visibleColumns)}
                onToggleColumn={toggleColumn}
              />
            </>
          }
        />

        <Tabs items={pageTabs} activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === 'calendar' && (
          <>
            <CalendarFilters
              includeCompleted={calendarFilters.includeCompleted}
              division={calendarFilters.division}
              tier={calendarFilters.tier}
              assessorId={calendarFilters.assessorId}
              status={calendarFilters.status}
              divisionOptions={divisionOptions}
              assessorOptions={assessorOptions}
              onFilterChange={handleCalendarFilterChange}
            />

            <MeetingsCalendar
              events={calendarData?.events || []}
              onDatesChange={handleCalendarDatesChange}
              isLoading={calendarIsLoading || calendarIsFetching}
            />
          </>
        )}

        {activeTab === 'list' && (
          <>
            {/* Status Filters */}
            <FilterButtons
              filters={statusFilters}
              activeFilter={statusFilter}
              onFilterChange={handleFilterChange}
              filterCounts={filterCounts}
              showCheckbox={true}
              dataTestId="assessor-meetings-filter"
              checkboxLabel="Show only my meetings"
              checkboxChecked={myMeetingsOnly}
              onCheckboxChange={handleMyMeetingsChange}
            />

            {/* Show spinner overlay when fetching data (filter changes) */}
            {isFetching && !isLoading && <LoadingOverlay variant="page" />}

            {isLoading ? (
              <LoadingState message="Loading meetings..." />
            ) : (
              <>
                {/* Always show table with filters so user can clear them even when no results */}
                <AssessorMeetingsTable
                  scheduleItems={scheduleItems}
                  columns={renderedColumns}
                  visibleColumns={visibleColumns}
                  onScheduleDate={handleScheduleDate}
                  onAssignRole={handleAssignRole}
                  onCreateAssessment={handleCreateAssessment}
                  onAddToGroup={handleAddToGroup}
                  onRemoveFromGroup={handleRemoveFromGroup}
                  onColumnSort={handleColumnSort}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  getSortIndicator={getSortIndicator}
                  isColumnSorted={isColumnSorted}
                  columnFilters={{
                    values: columnFilterValues,
                    onChange: setColumnFilter,
                    onClearAll: clearColumnFilters,
                    hasActiveFilters: hasActiveColumnFilters
                  }}
                  isEmpty={scheduleItems.length === 0}
                />

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  itemName="meetings"
                  pageSizeOptions={[25, 50, 100]}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Reschedule Meeting Modal */}
      {showScheduledDateModal && selectedItem?.contracts[0] && (
        <ScheduledDateModal
          contract={selectedItem.contracts[0]}
          meetingId={selectedItem.id || 0}
          currentDate={selectedItem.meeting?.scheduled_datetime || ''}
          onClose={closeScheduledDateModal}
          onDateSelected={handleDateSelected}
          dataTestId="scheduled-date"
        />
      )}

      {/* Assessor Reassignment Modal */}
      {showAssessorModal && selectedItem && (
        <AssessorAssignmentModal
          scheduleItem={selectedItem}
          isReassignment={!!selectedItem.assessor}
          onClose={closeAssessorModal}
          onAssigned={handleAssessorAssigned}
          dataTestId="assessor-assignment"
        />
      )}

      {/* Contract Manager Assignment Modal */}
      {showManagerModal &&
        selectedItem &&
        selectedContractId &&
        (() => {
          const contract = selectedItem.contracts.find((c) => c.id === selectedContractId);
          return contract ? (
            <MultiRoleAssignmentModal
              contractNumber={contract.contract_number}
              supplierName={contract.supplier_name}
              roleType="manager"
              currentAssigneeIds={contract.contract_managers?.map((m) => m.id) || []}
              onClose={closeManagerModal}
              onAssigned={handleManagerAssigned}
              dataTestId="manager-assignment"
            />
          ) : null;
        })()}

      {/* Contract Owner Assignment Modal */}
      {showOwnerModal &&
        selectedItem &&
        selectedContractId &&
        (() => {
          const contract = selectedItem.contracts.find((c) => c.id === selectedContractId);
          return contract ? (
            <MultiRoleAssignmentModal
              contractNumber={contract.contract_number}
              supplierName={contract.supplier_name}
              roleType="owner"
              currentAssigneeIds={contract.contract_owners?.map((o) => o.id) || []}
              division={contract.division}
              onClose={closeOwnerModal}
              onAssigned={handleOwnerAssigned}
              dataTestId="owner-assignment"
            />
          ) : null;
        })()}

      {/* Add to Group Modal */}
      {showAddToGroupModal && groupToAddTo?.id && (
        <AddToGroupModal
          title="Add Contracts to Meeting Group"
          groupMetadata={{
            tier: groupToAddTo.contracts[0]?.tier || 'N/A',
            assessor: groupToAddTo.assessor?.name || undefined,
            status: groupToAddTo.meeting?.scheduled_datetime ? 'Scheduled' : 'Not Scheduled'
          }}
          groupId={groupToAddTo.id}
          queryKey={[
            'compatible-contracts',
            groupToAddTo.id?.toString() || '',
            'ready_for_scheduling',
            groupToAddTo.assessor?.id?.toString() || ''
          ]}
          queryFn={() =>
            schedulerService.getCompatibleContracts({
              state: 'ready_for_scheduling',
              assessorId: groupToAddTo.assessor?.id,
              managerIds: groupToAddTo.contracts[0]?.contract_managers?.map((m) => m.id) || [],
              groupId: groupToAddTo.id
            })
          }
          onClose={closeAddToGroupModal}
          onAdd={handleAddContractsToGroup}
          dataTestId="add-to-group"
        />
      )}

      {/* Remove from Group Confirmation */}
      {showRemoveConfirm && (
        <ConfirmDialog
          isOpen={showRemoveConfirm}
          title="Remove Contract from Group?"
          message="This contract will be removed from the group and become a separate meeting. This action cannot be undone."
          confirmText="Remove"
          cancelText="Cancel"
          confirmVariant="danger"
          onConfirm={confirmRemoveFromGroup}
          onCancel={closeRemoveConfirm}
        />
      )}
    </PageLayout>
  );
};

export default AssessorMeetingsPage;
